/* eslint-disable unicorn/filename-case -- TODO rename later */
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import { type NoAccessReason, noAccessReasonFromAuthError } from "~/session/model/no-access";
import { NoAccessScreen } from "~/session/ui/no-access-screen";

import { IstariDigitalLogo } from "../../assets/svg";
import Button from "../../components/__deprecated/Button";

const FullScreenLoading = () => (
  <div role="status" aria-live="polite" data-testid="auth-loading-spinner">
    <span className="sr-only">Loading</span>
  </div>
);

interface AuthErrorBoundaryProps {
  children: React.ReactNode;
}

function AuthErrorBoundary({ children }: AuthErrorBoundaryProps) {
  const [hasAuthenticationErrors, setHasAuthenticationErrors] = useState(false);
  // Kept once seen: a later user load clears auth.error, and the refusal must outlive it until sign-out.
  const [refusedReason, setRefusedReason] = useState<NoAccessReason>();

  const auth = useAuth();

  useEffect(() => {
    if (auth && auth.isAuthenticated) {
      window.localStorage.setItem("istari-user", JSON.stringify(auth.user));
    }
    if (auth && auth.error) {
      setHasAuthenticationErrors(true);
    }
    const reason = noAccessReasonFromAuthError(auth?.error);
    if (reason) setRefusedReason(reason);
  }, [auth, auth.isAuthenticated, auth.error]);

  switch (auth.activeNavigator) {
    case "signinSilent":
    case "signoutRedirect":
      return <FullScreenLoading />;
    default:
  }

  if (auth.isLoading) {
    return <FullScreenLoading />;
  }

  // A refused login must not offer a reload, which would only loop through the identity provider again.
  const noAccessReason = refusedReason ?? noAccessReasonFromAuthError(auth.error);
  if (noAccessReason) {
    return <NoAccessScreen reason={noAccessReason} onSignOut={() => void auth.signoutRedirect()} />;
  }

  if (hasAuthenticationErrors) {
    return (
      <div className="flex h-screen w-full justify-center bg-[#f3f3f3] text-center">
        <div className="mt-[50px]">
          <IstariDigitalLogo height="40px" />
          <div className="justify-center gap-y-[10px]">
            <h4 className="text-[2rem] leading-[125%] font-bold text-gray-800">You are signed out.</h4>
            <span className="text-sm font-semibold text-gray-700">Please click the button to log in again.</span>
          </div>
          <div className="mt-[30px]">
            <Button fullWidth label="Login" variant="primary" onClick={() => window.location.reload()} />
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export default AuthErrorBoundary;
